import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL, ENABLE_LOG } from '../config';

const gradosIniciales = [
  'Teniente',
  'Sargento',
  'Capitán',
  'Coronel',
  'General',
];

const ROLES = [
  { id: 'admin', nombre: 'Administrador', permisos: ['todo'] },
  { id: 'juez', nombre: 'Juez', permisos: ['ver_acusados','editar_estado','ver_documentos','generar_documentos'] },
  { id: 'fiscal', nombre: 'Fiscal', permisos: ['ver_acusados','agregar_testimonio','registrar_audio','generar_documentos'] },
  { id: 'secretario', nombre: 'Secretario', permisos: ['ver_acusados','ver_documentos'] },
];

const PERMISOS = [
  { id: 'ver_acusados', label: 'Ver acusados' },
  { id: 'editar_estado', label: 'Editar estado procesal' },
  { id: 'agregar_testimonio', label: 'Agregar testimonio' },
  { id: 'registrar_audio', label: 'Registrar audio' },
  { id: 'generar_documentos', label: 'Generar documentos' },
  { id: 'ver_documentos', label: 'Ver documentos generados' },
  { id: 'configurar_sistema', label: 'Configurar sistema' },
];

export default function Configuracion({ onVolver }) {
  const [grados, setGrados] = useState(gradosIniciales);
  const [nuevoGrado, setNuevoGrado] = useState('');
  const [logo, setLogo] = useState(() => localStorage.getItem('logo_institucional') || '');
  const [logoErr, setLogoErr] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [formUsuario, setFormUsuario] = useState({ usuario: '', nombre: '', email: '', password: '', rol: 'secretario', permisos: [] });
  const [editId, setEditId] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });

  const getBackendUrl = () => BACKEND_URL?.trim().replace(/\/$/, '');

  const loadUsuarios = async () => {
    const cleanUrl = getBackendUrl();
    if (!cleanUrl) {
      setStatus({ type: 'danger', message: 'BACKEND_URL no configurado.' });
      return;
    }
    try {
      const response = await axios.get(`${cleanUrl}/api/users`);
      setUsuarios(response.data || []);
    } catch (err) {
      if (ENABLE_LOG) console.error('Error cargando usuarios:', err.response?.data || err.message);
      setStatus({ type: 'danger', message: 'No se pudieron cargar usuarios.' });
    }
  };

  useEffect(() => {
    loadUsuarios();
  }, []);

  const handleAgregarGrado = (e) => {
    e.preventDefault();
    if (nuevoGrado && !grados.includes(nuevoGrado)) {
      setGrados([...grados, nuevoGrado]);
      setNuevoGrado('');
    }
  };

  // Solo gestión de grados en esta pantalla.
  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const valid = ['image/png','image/jpeg','image/svg+xml','image/webp'];
    if (!valid.includes(file.type)) { setLogoErr('Formato no soportado'); return; }
    if (file.size > 2*1024*1024) { setLogoErr('Imagen > 2MB'); return; }
    setLogoErr('');
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result;
      setLogo(data);
      localStorage.setItem('logo_institucional', data);
    };
    reader.readAsDataURL(file);
  };

  const borrarLogo = () => {
    setLogo('');
    localStorage.removeItem('logo_institucional');
  };

  return (
    <div className="container py-4">
      <button className="btn btn-outline-primary btn-volver mb-3" onClick={onVolver}>
        <i className="bi bi-arrow-left"></i>
        <span>Volver</span>
      </button>
      <div className="d-flex flex-column gap-4">
        <div>
          <div className="card mb-3">
            <div className="card-header">Logo institucional</div>
            <div className="card-body">
              <input type="file" className="form-control" accept="image/*" onChange={handleLogoChange} />
              {logoErr && <div className="text-danger small mt-2">{logoErr}</div>}
              {logo && (
                <div className="mt-2 d-flex align-items-start gap-2">
                  <img src={logo} alt="Logo" style={{ width: 96, height: 96, objectFit: 'contain', border: '1px solid #eee', borderRadius: 8 }} />
                  <button className="btn btn-sm btn-outline-danger" onClick={borrarLogo} type="button">Quitar</button>
                </div>
              )}
              {!logo && <div className="text-muted small mt-2">PNG/JPG/SVG/WEBP (máx. 2MB).</div>}
            </div>
          </div>
        </div>
        <div>
          <div className="card mb-3">
            <div className="card-header">Grados disponibles</div>
            <div className="card-body">
              <ul className="list-group mb-3">
                {grados.map(g => <li className="list-group-item" key={g}>{g}</li>)}
              </ul>
              <form onSubmit={handleAgregarGrado} className="input-group">
                <input className="form-control" placeholder="Nuevo grado" value={nuevoGrado} onChange={e => setNuevoGrado(e.target.value)} />
                <button className="btn btn-success" type="submit">Agregar</button>
              </form>
            </div>
          </div>
        </div>
        <div>
          <div className="card mb-3">
            <div className="card-header">Usuarios del sistema</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <form className="row g-2" onSubmit={async (e) => {
                    e.preventDefault();
                    const cleanUrl = getBackendUrl();
                    if (!cleanUrl) {
                      setStatus({ type: 'danger', message: 'BACKEND_URL no configurado.' });
                      return;
                    }
                    if (!formUsuario.usuario || !formUsuario.nombre || !formUsuario.email) return;
                    try {
                      setStatus({ type: '', message: '' });
                      if (!editId) {
                        const response = await axios.post(`${cleanUrl}/api/users`, formUsuario);
                        setUsuarios(prev => [...prev, response.data]);
                      } else {
                        const payload = { ...formUsuario };
                        if (!payload.password) delete payload.password;
                        const response = await axios.put(`${cleanUrl}/api/users/${editId}`, payload);
                        setUsuarios(prev => prev.map(u => u.id === editId ? response.data : u));
                        setEditId('');
                      }
                      setFormUsuario({ usuario: '', nombre: '', email: '', password: '', rol: 'secretario', permisos: [] });
                    } catch (err) {
                      if (ENABLE_LOG) console.error('Error guardando usuario:', err.response?.data || err.message);
                      setStatus({ type: 'danger', message: err.response?.data?.error || 'No se pudo guardar el usuario.' });
                    }
                  }}>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Usuario</label>
                      <input className="form-control" value={formUsuario.usuario} onChange={e => setFormUsuario({ ...formUsuario, usuario: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Nombre</label>
                      <input className="form-control" value={formUsuario.nombre} onChange={e => setFormUsuario({ ...formUsuario, nombre: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Email</label>
                      <input className="form-control" type="email" value={formUsuario.email} onChange={e => setFormUsuario({ ...formUsuario, email: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Contraseña</label>
                      <input className="form-control" type="password" value={formUsuario.password} onChange={e => setFormUsuario({ ...formUsuario, password: e.target.value })} placeholder={editId ? 'Dejar en blanco para mantener' : ''} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Rol</label>
                      <select className="form-select" value={formUsuario.rol} onChange={e => {
                        const rolSel = e.target.value;
                        const rolObj = ROLES.find(r => r.id === rolSel);
                        const perms = rolObj?.permisos?.includes('todo') ? PERMISOS.map(p => p.id) : (rolObj?.permisos || []);
                        setFormUsuario({ ...formUsuario, rol: rolSel, permisos: perms });
                      }}>
                        {ROLES.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Permisos</label>
                      <div className="row g-2">
                        {PERMISOS.map(p => (
                          <div className="col-12 col-md-6" key={p.id}>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" id={`perm_${p.id}`} checked={formUsuario.permisos.includes(p.id)} onChange={(e) => {
                                const checked = e.target.checked;
                                setFormUsuario(prev => ({
                                  ...prev,
                                  permisos: checked ? [...new Set([...prev.permisos, p.id])] : prev.permisos.filter(x => x !== p.id)
                                }));
                              }} />
                              <label className="form-check-label" htmlFor={`perm_${p.id}`}>{p.label}</label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-12 d-flex gap-2">
                      <button className="btn btn-primary" type="submit">{editId ? 'Guardar cambios' : 'Crear usuario'}</button>
                      {editId && <button className="btn btn-outline-secondary" type="button" onClick={() => { setEditId(''); setFormUsuario({ usuario: '', nombre: '', email: '', password: '', rol: 'secretario', permisos: [] }); }}>Cancelar</button>}
                    </div>
                  </form>
                  {status.message && (
                    <div className={`alert alert-${status.type} small mt-3`}>{status.message}</div>
                  )}
                </div>
                <div className="col-12 col-lg-6">
                  <ul className="list-group">
                    {usuarios.map(u => (
                      <li key={u.id} className="list-group-item d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{u.nombre} <span className="badge bg-secondary ms-2">{u.rol}</span></div>
                          <div className="small text-muted">{u.usuario} · {u.email}</div>
                          <div className="small">Permisos: {u.permisos.includes('todo') ? 'Todos' : (u.permisos.join(', ') || '—')}</div>
                        </div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditId(u.id); setFormUsuario({ usuario: u.usuario || '', nombre: u.nombre || '', email: u.email || '', password: '', rol: u.rol || 'secretario', permisos: u.permisos || [] }); }}>Editar</button>
                          <button className="btn btn-sm btn-outline-danger" onClick={async () => {
                            const cleanUrl = getBackendUrl();
                            if (!cleanUrl) {
                              setStatus({ type: 'danger', message: 'BACKEND_URL no configurado.' });
                              return;
                            }
                            try {
                              await axios.delete(`${cleanUrl}/api/users/${u.id}`);
                              setUsuarios(prev => prev.filter(x => x.id !== u.id));
                            } catch (err) {
                              if (ENABLE_LOG) console.error('Error eliminando usuario:', err.response?.data || err.message);
                              setStatus({ type: 'danger', message: err.response?.data?.error || 'No se pudo eliminar el usuario.' });
                            }
                          }}>Eliminar</button>
                        </div>
                      </li>
                    ))}
                    {usuarios.length === 0 && <li className="list-group-item text-muted">No hay usuarios.</li>}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
